import type { PlatformAdapter, ProductData } from '../adapters/PlatformAdapter';
import type { LocationService, NavigatorService, DeviceType } from './browser';
import type { TrackingDebugService } from './TrackingDebugService';

/** Cached PDP URL, product ids, UTM params, and coarse UA-derived device/os/browser — refreshed on SPA-style navigations when the adapter supports it. */
export interface PageContext {
  page: {
    type: 'product-detail';
    url: string;
    utm_campaign: string | null;
    utm_source: string | null;
    utm_medium: string | null;
  };
  pageData: ProductData;
  userAgent: string;
  device_type: DeviceType;
  viewport_height: number;
  os: string | null;
  browser: string | null;
}

function getUtm(
  params: URLSearchParams,
  key: string
): string | null {
  const value = params.get(key);

  return value && value.length > 0 ? value : null;
}

/** Best-effort string for analytics only — not a security or compatibility signal. */
function guessOsFromUa(ua: string): string | null {
  const s = ua.toLowerCase();
  if (s.includes('windows')) {
    return 'Windows';
  }
  if (s.includes('mac os') || s.includes('macintosh')) {
    return 'macOS';
  }
  if (s.includes('android')) {
    return 'Android';
  }
  if (s.includes('iphone') || s.includes('ipad') || s.includes('ios')) {
    return 'iOS';
  }
  if (s.includes('linux')) {
    return 'Linux';
  }

  return null;
}

/** Same caveats as {@link guessOsFromUa}; UA sniffing is inherently fuzzy. */
function guessBrowserFromUa(ua: string): string | null {
  const s = ua.toLowerCase();
  if (s.includes('edg/')) {
    return 'Edge';
  }
  if (s.includes('chrome/') && !s.includes('edg/') && !s.includes('opr/')) {
    return 'Chrome';
  }
  if (s.includes('safari/') && !s.includes('chrome/')) {
    return 'Safari';
  }
  if (s.includes('firefox/')) {
    return 'Firefox';
  }
  if (s.includes('opr/') || s.includes('opera')) {
    return 'Opera';
  }

  return null;
}

/**
 * Loads {@link PageContext} once per “logical” product page via {@link PlatformAdapter.getProductData}.
 * Shopware storefront today is mostly full reloads; `refresh()` exists for adapters that signal SPA navigations.
 */
export class PageContextService {
  private context: PageContext | null = null;

  constructor(
    private readonly adapter: PlatformAdapter,
    private readonly location: LocationService,
    private readonly navigator: NavigatorService,
    private readonly debug?: TrackingDebugService,
  ) {}

  async init(): Promise<void> {
    const url = this.location.getUrl();
    const params = this.location.getSearchParams();
    const ua = this.navigator.getUserAgent();

    const pageData = await this.adapter.getProductData();

    this.context = {
      page: {
        type: 'product-detail',
        url,
        utm_campaign: getUtm(params, 'utm_campaign'),
        utm_source: getUtm(params, 'utm_source'),
        utm_medium: getUtm(params, 'utm_medium'),
      },
      pageData,
      userAgent: ua,
      device_type: this.navigator.getDeviceType(),
      viewport_height: window.innerHeight,
      os: guessOsFromUa(ua),
      browser: guessBrowserFromUa(ua),
    };

    this.debug?.log('PageContextService', 'init', {
      device_type: this.context.device_type,
      utmAny: !!(this.context.page.utm_campaign ?? this.context.page.utm_source ?? this.context.page.utm_medium),
      product_id: this.context.pageData.product_id,
      priceCents: this.context.pageData.price,
    });
  }

  async refresh(): Promise<void> {
    this.debug?.log('PageContextService', 'refresh', {});
    await this.init();
  }

  get(): PageContext {
    if (!this.context) {
      throw new Error('PageContextService not initialised');
    }

    return this.context;
  }
}
